/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';

/**
 * Returns the current theme styles of an SC sensor event history row (Shock
 * and Tilt) - each row renders as the shared bordered tile, the reading
 * label maps to the shared uppercase label style, and the event and time
 * lines map to the shared `meta` caption style.
 *
 * - `historyItem`: the bordered tile row.
 * - `reading`: the uppercase reading label.
 * - `eventText`: the event line - each sensor keeps its own status color.
 * - `historyItemTime`: the event time line.
 *
 * Returned as style objects, not a `makeStyles` hook, so they are spread
 * into each history row's own sheet - a separate shared sheet is created
 * before the importing component's sheet and so would lose to its legacy
 * classes.
 *
 * ```typescript
 *
 *   import {getCurrentSensorHistoryStyles} from './sensorHistoryStyles';
 *   ...
 *
 *   const useStyles = makeStyles(() => {
 *     const {historyItem} = getCurrentSensorHistoryStyles();
 *     return {
 *       currentHistoryItem : historyItem
 *     };
 *   });
 *
 * ```
 *
 * @returns The `historyItem`, `reading`, `eventText` and `historyItemTime`
 *          style objects.
 */
export const getCurrentSensorHistoryStyles = () => {
  const {label, meta, tile} = getAssetDetailsContentStyles();
  return {
    historyItem : {
      ...tile,
      display       : 'flex',
      flexDirection : 'column' as const,
      gap           : '8px'
    },
    reading         : {...label},
    eventText       : {...meta},
    historyItemTime : {...meta}
  };
};

export default getCurrentSensorHistoryStyles;
